// Cha Cha Cha runner — a TEAM game where each group is a team.
//
// The number of best net (or gross) scores that count rotates every three
// holes: 1st hole → best 1, 2nd → best 2, 3rd → best 3, 4th → best 1, and so
// on. The team's total is the sum across all holes played; lowest total wins.
//
// Lone Ranger (optional): a designated player — rotating by hole through the
// group — MUST count on their assigned holes regardless of being among the
// best. e.g. in a four-player group, player A counts on holes 1, 5, 9, 13, 17.
// The remaining counters for that hole are filled with the best of the rest.
//
// Pure standings only (no betting payouts yet). Operates over the whole field.

import { Course, Hole, Player, ScoreMap } from "../types";
import { TeamSegmentScores } from "./teamBets";

function courseHandicap(
  handicapIndex: number | undefined,
  slope: number | undefined,
  rating: number | undefined,
  totalPar: number,
): number {
  if (handicapIndex == null) return 0;
  const slopeFactor = slope ? slope / 113 : 1;
  const ratingDiff = rating != null ? rating - totalPar : 0;
  return Math.round(handicapIndex * slopeFactor + ratingDiff);
}

function strokesOnHole(courseHcp: number, hole: Hole): number {
  if (courseHcp <= 0) return 0;
  const base = Math.floor(courseHcp / 18);
  const remainder = courseHcp % 18;
  return base + (hole.si <= remainder ? 1 : 0);
}

// 1 → 2 → 3 → 1 → ... by 0-based hole position.
function countersForIndex(i: number): number {
  return (i % 3) + 1;
}

export interface ChaChaChaOptions {
  scoringMode?: "gross" | "net";
  loneRanger?: boolean;
}

export interface ChaChaChaGroupInput {
  groupId: string;
  groupName?: string;
  players: Player[];
  scores: ScoreMap;
}

export interface ChaChaChaInput {
  course: Course;
  groups: ChaChaChaGroupInput[];
  options: ChaChaChaOptions;
}

export interface ChaChaChaResult {
  teams: TeamSegmentScores[];
}

export function runChaChaCha(input: ChaChaChaInput): ChaChaChaResult {
  const { course, groups, options } = input;
  const useNet = options.scoringMode !== "gross"; // default net
  const loneRanger = options.loneRanger === true;
  const totalPar = course.holes.reduce((s, h) => s + h.par, 0);
  const tees = course.tees || [];

  const teams: TeamSegmentScores[] = groups.map((g) => {
    const hcpById: Record<string, number> = {};
    for (const p of g.players) {
      const tee = tees.find((t) => t.id === p.tee);
      hcpById[p.id] = courseHandicap(
        p.handicap,
        tee?.slope,
        tee?.rating,
        totalPar,
      );
    }

    const valueOf = (playerId: string, hole: Hole): number | null => {
      const sc = (g.scores[playerId] || {})[hole.number];
      if (!sc || typeof sc.gross !== "number") return null;
      return useNet ? sc.gross - strokesOnHole(hcpById[playerId], hole) : sc.gross;
    };

    let overall = 0;
    let front = 0;
    let back = 0;
    let holesPlayed = 0;
    course.holes.forEach((hole, i) => {
      const scored = g.players
        .map((p) => ({ id: p.id, val: valueOf(p.id, hole) }))
        .filter((s): s is { id: string; val: number } => s.val != null);
      if (scored.length === 0) return;
      holesPlayed++;

      const n = countersForIndex(i);
      let counted: number[];

      const designated =
        loneRanger && g.players.length > 0
          ? g.players[(hole.number - 1) % g.players.length]
          : undefined;
      const designatedVal = designated
        ? scored.find((s) => s.id === designated.id)?.val
        : undefined;

      if (designated && designatedVal != null) {
        // Force the designated player in, fill the rest with the best others.
        const others = scored
          .filter((s) => s.id !== designated.id)
          .map((s) => s.val)
          .sort((a, b) => a - b);
        counted = [designatedVal, ...others.slice(0, Math.max(0, n - 1))];
      } else {
        counted = scored
          .map((s) => s.val)
          .sort((a, b) => a - b)
          .slice(0, n);
      }

      const contrib = counted.reduce((a, b) => a + b, 0);
      overall += contrib;
      if (hole.number <= 9) front += contrib;
      else back += contrib;
    });

    return {
      groupId: g.groupId,
      groupName: g.groupName,
      players: g.players,
      overall,
      front,
      back,
      holesPlayed,
    };
  });

  // Lowest combined score wins, but teams that haven't started (no holes
  // played, overall 0) sort to the bottom rather than falsely leading.
  teams.sort((a, b) => {
    if (a.holesPlayed === 0 && b.holesPlayed === 0) return 0;
    if (a.holesPlayed === 0) return 1;
    if (b.holesPlayed === 0) return -1;
    return a.overall - b.overall;
  });
  return { teams };
}
